import { useContext } from 'react';

import { SmartCardContext } from '@atlaskit/link-provider/context';
import {
	type StudioCrossProductBridge,
	useStudioCrossProductUrlWrapper,
} from '@atlassian/atlassian-studio-observability/cross-product-url';

export type AgentCrossProductUrlOptions = {
	bridge: StudioCrossProductBridge;
};

/**
 * Host-aware XPC URL wrapper for shared AI Mate agent components.
 *
 * These components render across multiple host products (Studio, and Rovo Chat
 * embedded in Jira/Confluence/Townsquare/...). The XPC `source` must reflect the
 * actual host, provided via `SmartCardProvider`'s `xpcProduct`. When no host
 * product is available (or no provider is present) the wrapper is an identity
 * no-op, so we never mis-attribute navigations (e.g. to `atlassianStudio`).
 */
export const useAgentCrossProductUrlWrapper = ({
	bridge,
}: AgentCrossProductUrlOptions): ((url: string) => string) => {
	// Read the context defensively: `useSmartLinkContext()` throws without a
	// provider, but these components can render outside one.
	const cardContext = useContext(SmartCardContext);
	const xpcProduct = cardContext?.xpcProduct;

	// Empty string (not undefined) so the Studio hook disables reporting rather
	// than falling back to its `atlassianStudio` default.
	return useStudioCrossProductUrlWrapper({ bridge, product: xpcProduct ?? '' });
};
